import { useGuide } from "@/lib/guide/context";
import { useT } from "@/i18n";
import {
  UserRound, Store, Users, Clock, Wallet, Rss, Layers,
  ClipboardList, CalendarCheck, Compass, Check, X, Sparkles,
} from "lucide-react";

const THEME_ICON = {
  profile: UserRound,
  offering: Store,
  circle: Users,
  availability: Clock,
  payments: Wallet,
  post: Rss,
  method: Layers,
  weekly_plan: ClipboardList,
  calendar: CalendarCheck,
  discover: Compass,
};

export function GuideBar() {
  const t = useT();
  const { barVisible, status, openTheme, hideBarSession } = useGuide();
  if (!barVisible || !status) return null;

  const themes = status.themes || [];
  const pct = status.total ? Math.round((status.completed / status.total) * 100) : 0;

  return (
    <div
      className="sticky top-16 z-30 border-b border-border/70 bg-gradient-to-r from-primary/10 via-card/95 to-card/95 backdrop-blur"
      data-testid="guide-bar"
    >
      <div className="container-page py-2.5">
        <div className="flex items-center gap-3">
          <div className="hidden shrink-0 items-center gap-2 sm:flex">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">{t("guide.bar.title")}</p>
              <p className="text-[11px] text-muted-foreground">
                {t("guide.bar.progress", { done: status.completed, total: status.total })}
              </p>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-x-auto">
            <div className="flex items-center gap-2 pb-0.5">
              {themes.map((th) => {
                const Icon = THEME_ICON[th.key] || Sparkles;
                const state = th.done ? "done" : th.skipped ? "skipped" : "open";
                const cls =
                  state === "done"
                    ? "border-transparent bg-primary/15 text-primary"
                    : state === "skipped"
                    ? "border-dashed border-border bg-transparent text-muted-foreground"
                    : "border-border bg-background text-foreground hover:bg-accent";
                return (
                  <button
                    key={th.key}
                    onClick={() => openTheme(th.key)}
                    data-testid={`guide-chip-${th.key}`}
                    aria-label={`${t(`guide.theme.${th.key}.label`)} — ${t(`guide.status.${state}`)}`}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${cls}`}
                  >
                    {state === "done" ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                    <span className="whitespace-nowrap">{t(`guide.theme.${th.key}.label`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hidden shrink-0 items-center md:flex">
            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-secondary" title={`${pct}%`}>
              <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <button
            onClick={hideBarSession}
            data-testid="guide-bar-hide"
            aria-label={t("guide.bar.hide")}
            title={t("guide.bar.hide")}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
